import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { AlertCircle } from 'lucide-react';
import PasswordInput from '../components/auth/PasswordInput';
import Logo from '../components/Logo';
import AppTitle from '../components/AppTitle';

export default function Login() {
  const navigate = useNavigate();
  const { user, loginUser, registerUser, requestPasswordReset } = useStore();
  const [isRegistering, setIsRegistering] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const validatePassword = (password: string): boolean => {
    const hasMinLength = password.length >= 8;
    const hasLetterAndNumber = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/.test(password);

    if (!hasMinLength || !hasLetterAndNumber) {
      setPasswordError('A senha deve conter pelo menos 8 caracteres, incluindo letras e números');
      return false;
    }

    setPasswordError('');
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;

    try {
      await loginUser(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const form = e.target as HTMLFormElement;
    const name = (form.elements.namedItem('name') as HTMLInputElement).value;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;

    if (!validatePassword(password)) {
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      setIsLoading(false);
      return;
    }

    try {
      await registerUser({ name, email, password });
      await loginUser(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;

    try {
      await requestPasswordReset(email);
      alert('Email de recuperação enviado. Verifique sua caixa de entrada.');
      setShowResetPassword(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-950 to-navy-900 flex items-center justify-center p-4">
      {/* Efeito de fundo decorativo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent-400/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full">
        {/* Card com glassmorphism */}
        <div className="bg-navy-800/60 backdrop-blur-xl rounded-2xl shadow-2xl border border-navy-700/50 p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="p-3 bg-navy-900/50 rounded-2xl border border-navy-700/50 mb-4">
              <Logo className="h-16 w-auto" />
            </div>
            <AppTitle className="text-2xl font-bold text-gradient-gold" />
            <p className="text-navy-300 text-sm mt-2">Análise Jurídica com Inteligência Artificial</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error-500/20 border border-error-500/50 text-error-300 rounded-lg flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {showResetPassword ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-navy-200 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-navy-900/50 border border-navy-600 text-navy-100 placeholder-navy-500 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30 transition-all duration-200"
                  placeholder="seu@email.com"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-accent py-3 rounded-lg font-semibold"
              >
                {isLoading ? 'Enviando...' : 'Enviar Email de Recuperação'}
              </button>
              <button
                type="button"
                onClick={() => setShowResetPassword(false)}
                disabled={isLoading}
                className="w-full text-navy-300 hover:text-accent-400 py-2 transition-colors duration-200"
              >
                Voltar ao Login
              </button>
            </form>
          ) : isRegistering ? (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-navy-200 mb-1">
                  Nome Completo
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-navy-900/50 border border-navy-600 text-navy-100 placeholder-navy-500 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30 transition-all duration-200"
                  placeholder="Seu nome"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-navy-200 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-navy-900/50 border border-navy-600 text-navy-100 placeholder-navy-500 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30 transition-all duration-200"
                  placeholder="seu@email.com"
                />
              </div>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                label="Senha"
                error={passwordError}
                required
              />
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                label="Confirmar Senha"
                error={password !== confirmPassword ? 'As senhas não coincidem' : ''}
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-accent py-3 rounded-lg font-semibold"
              >
                {isLoading ? 'Cadastrando...' : 'Cadastrar'}
              </button>
              <button
                type="button"
                onClick={() => setIsRegistering(false)}
                disabled={isLoading}
                className="w-full text-navy-300 hover:text-accent-400 py-2 transition-colors duration-200"
              >
                Já tem uma conta? Faça login
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-navy-200 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-navy-900/50 border border-navy-600 text-navy-100 placeholder-navy-500 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30 transition-all duration-200"
                  placeholder="seu@email.com"
                />
              </div>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                label="Senha"
                required
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="text-sm text-accent-400 hover:text-accent-300 transition-colors duration-200"
                >
                  Esqueceu a senha?
                </button>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-accent py-3 rounded-lg font-semibold"
              >
                {isLoading ? 'Entrando...' : 'Entrar'}
              </button>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-navy-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-navy-800/60 text-navy-400">ou</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsRegistering(true)}
                disabled={isLoading}
                className="w-full btn-outline py-3 rounded-lg font-semibold"
              >
                Criar nova conta
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
